<script setup>
// SiteUp: Brasa, o vaga-lume do onboarding. Corpo em gota inclinada, duas
// asas em folha herdadas da seta dupla do logo, e a luz no abdomen, embaixo.
// A luz e a unica fonte de brilho do personagem: em `point` ela vira facho
// na direcao do alvo e faz par com o holofote do useFocusSpotlight.
//
// Especificacao aprovada pelo Lucas em 23/08/2026 (folha de personagem):
// .hermes/mockups/2026-08-23-brasa-folha-de-personagem.html
//
// Animacao e SMIL dentro do proprio SVG, como o AnimatedLineIcon, de
// proposito: zero CSS custom, zero keyframe global no tailwind, e o estado
// e uma troca de `key` no <svg>, entao animacao de um tiro so (land, point,
// celebrate, nudge) recomeca toda vez que o estado entra de novo.
import { computed, onBeforeUnmount, onMounted, ref, useId } from 'vue';
import { prefersReducedMotion } from 'dashboard/composables/useFocusMotion';
import { BRASA_STATES } from './brasaStates';
import { BRASA_SHAPE } from '../brasa/brasaShape';

const props = defineProps({
  state: {
    type: String,
    default: 'idle',
    validator: value => BRASA_STATES.includes(value),
  },
  // Rumo do olhar em graus: 0 = cima, sentido horario (90 = direita).
  // null = olhar neutro.
  look: {
    type: Number,
    default: null,
  },
  // Rumo do facho em `point`, mesma convencao do `look`. null = reto pra baixo.
  aim: {
    type: Number,
    default: null,
  },
  // Espelha o rig (voar pra esquerda).
  flip: {
    type: Boolean,
    default: false,
  },
  size: {
    type: Number,
    default: 40,
  },
  // Forca animacao ligada/desligada; null segue prefers-reduced-motion.
  animated: {
    type: Boolean,
    default: null,
  },
});

const uid = useId();
const softId = `brasa-soft-${uid}`;
const soft2Id = `brasa-soft2-${uid}`;

// Movimento reduzido tem que valer na hora: quem liga a preferencia no sistema
// com o painel ja aberto passa mal agora, nao depois de um F5. Por isso o valor
// e reativo e a media query fica escutada enquanto o mascote existe.
const reducedMotion = ref(prefersReducedMotion());
let motionQuery = null;
const onMotionChange = event => {
  reducedMotion.value = event.matches;
};

onMounted(() => {
  // Ambiente sem matchMedia (jsdom, SSR): fica so a leitura do primeiro render.
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function')
    return;
  motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  motionQuery.addEventListener('change', onMotionChange);
});

onBeforeUnmount(() => {
  motionQuery?.removeEventListener('change', onMotionChange);
  motionQuery = null;
});

const anim = computed(() => {
  if (props.animated !== null) return props.animated;
  return !reducedMotion.value;
});

const is = s => props.state === s;

// Olhar: olhos deslizam, corpo inclina ate 12 graus e a asa do lado sobe.
const gaze = computed(() => {
  if (props.look === null) return { ex: 0, ey: 0, tilt: 0, wl: 0, wr: 0 };
  const a = (props.look * Math.PI) / 180;
  const dx = Math.sin(a);
  const dy = -Math.cos(a);
  return {
    ex: +(dx * 2.6).toFixed(2),
    ey: +(dy * 2.2).toFixed(2),
    tilt: +(dx * 12).toFixed(1),
    wl: +(-dy * 6).toFixed(1),
    wr: +(dy * 6).toFixed(1),
  };
});

// O facho nasce apontando pra baixo (rumo 180); pra mirar no rumo B gira B-180.
const beamRotate = computed(() => {
  const aim = props.aim === null ? 180 : props.aim;
  return `rotate(${((aim - 180) % 360).toFixed(1)} 56 84)`;
});

const rootTransform = computed(() => (props.flip ? 'scale(-1 1)' : null));
const rootViewBox = computed(() =>
  props.flip ? '-120 0 120 120' : '0 0 120 120'
);

const rigTransform = computed(() => `rotate(${gaze.value.tilt} 58 70)`);

const moverTransform = computed(() => {
  if (is('fly')) return 'rotate(-10 58 70)';
  if (is('sleep')) return 'translate(0 4) rotate(6 58 70)';
  if (is('wait')) return 'rotate(5 58 70)';
  if (is('inspect')) return 'rotate(-7 58 70) translate(2 2)';
  if (is('droop')) return 'rotate(8 58 70) translate(0 3)';
  return null;
});

const wingLTransform = computed(() => {
  if (is('sleep')) return 'rotate(-28 60 56)';
  if (is('droop')) return 'rotate(-16 60 56)';
  return `rotate(${gaze.value.wl} 60 56)`;
});

const wingRTransform = computed(() => {
  if (is('sleep')) return 'rotate(28 63 55)';
  if (is('droop')) return 'rotate(16 63 55)';
  return `rotate(${gaze.value.wr} 63 55)`;
});

const eyesTransform = computed(() => {
  const extraY = is('droop') ? 2 : 0;
  return `translate(${gaze.value.ex} ${gaze.value.ey + extraY})`;
});

// Palpebra: escala vertical em torno do centro de cada olho.
const eyeScaleY = computed(() => {
  if (is('sleep')) return 0.16;
  if (is('inspect')) return 0.55;
  return 1;
});
const eyeTransform = cy => {
  const k = eyeScaleY.value;
  if (k === 1) return null;
  return `translate(0 ${(cy * (1 - k)).toFixed(2)}) scale(1 ${k})`;
};

const haloOpacity = computed(() => {
  if (is('sleep')) return 0.07;
  if (is('wait')) return 0.4;
  if (is('fly')) return 0.7;
  if (is('hover')) return 0.65;
  if (is('inspect')) return 0.85;
  if (is('point')) return anim.value ? 0.5 : 0.95;
  return 0.55;
});
const haloR = computed(() => {
  if (is('inspect')) return 10.4;
  if (is('point') && !anim.value) return 22.75;
  return 13;
});
const coreR = computed(() => {
  if (is('inspect')) return 7.5;
  if (is('point') && !anim.value) return 8.125;
  return 6.5;
});
const coreOpacity = computed(() => (is('sleep') ? 0.22 : 1));
const beamOpacity = computed(() => (is('point') && !anim.value ? 0.26 : 0));

const wingsSlow = computed(
  () =>
    anim.value &&
    ['idle', 'point', 'inspect', 'wait', 'wave'].includes(props.state)
);
const wingsFast = computed(
  () => anim.value && ['fly', 'hover'].includes(props.state)
);
const wingSlowDur = computed(() => {
  if (is('point')) return '2.4s';
  if (is('inspect')) return '2.8s';
  if (is('wait')) return '4s';
  return '3.2s';
});
</script>

<template>
  <svg
    :key="state"
    class="block overflow-visible"
    :width="size"
    :height="size"
    :viewBox="rootViewBox"
    aria-hidden="true"
    data-testid="brasa-mascot"
    :data-state="state"
  >
    <defs>
      <filter :id="softId" x="-120%" y="-120%" width="340%" height="340%">
        <feGaussianBlur stdDeviation="5" />
      </filter>
      <filter :id="soft2Id" x="-60%" y="-60%" width="220%" height="220%">
        <feGaussianBlur stdDeviation="7" />
      </filter>
    </defs>
    <g :transform="rootTransform">
      <g :transform="rigTransform">
        <g :transform="moverTransform">
          <!-- movimento do corpo por estado -->
          <template v-if="anim">
            <animateTransform
              v-if="is('idle') || is('wave')"
              attributeName="transform"
              type="translate"
              values="0 0;0 -2;0 0"
              keyTimes="0;.5;1"
              calcMode="spline"
              keySplines=".42 0 .58 1;.42 0 .58 1"
              dur="3.2s"
              repeatCount="indefinite"
              additive="sum"
            />
            <animateTransform
              v-else-if="is('sleep')"
              attributeName="transform"
              type="translate"
              values="0 0;0 -1.5;0 0"
              keyTimes="0;.5;1"
              calcMode="spline"
              keySplines=".42 0 .58 1;.42 0 .58 1"
              dur="4.6s"
              repeatCount="indefinite"
              additive="sum"
            />
            <animateTransform
              v-else-if="is('fly')"
              attributeName="transform"
              type="translate"
              values="0 1;0 -3;0 1"
              keyTimes="0;.5;1"
              calcMode="spline"
              keySplines=".42 0 .58 1;.42 0 .58 1"
              dur=".84s"
              repeatCount="indefinite"
              additive="sum"
            />
            <animateTransform
              v-else-if="is('hover')"
              attributeName="transform"
              type="translate"
              values="0 2;0 -2;0 2"
              keyTimes="0;.5;1"
              calcMode="spline"
              keySplines=".42 0 .58 1;.42 0 .58 1"
              dur=".72s"
              repeatCount="indefinite"
              additive="sum"
            />
            <animateTransform
              v-else-if="is('land')"
              attributeName="transform"
              type="translate"
              values="0 -28;0 2;0 0"
              keyTimes="0;.7;1"
              dur=".9s"
              calcMode="spline"
              keySplines=".2 .8 .2 1;.2 .8 .2 1"
              fill="freeze"
              additive="sum"
            />
            <animateTransform
              v-else-if="is('point')"
              attributeName="transform"
              type="rotate"
              values="0 58 70;-8 58 70"
              keyTimes="0;1"
              calcMode="spline"
              keySplines="0 0 .58 1"
              dur="1.1s"
              fill="freeze"
              additive="sum"
            />
            <animateTransform
              v-else-if="is('celebrate')"
              attributeName="transform"
              type="translate"
              values="0 0;0 -11;0 0;0 -6;0 0;0 0"
              calcMode="spline"
              keySplines=".3 .7 .3 1;.3 .7 .3 1;.3 .7 .3 1;.3 .7 .3 1;.3 .7 .3 1"
              keyTimes="0;.2;.4;.56;.72;1"
              dur="1.4s"
              fill="freeze"
              additive="sum"
            />
            <animateTransform
              v-else-if="is('nudge')"
              attributeName="transform"
              type="translate"
              values="0 0;-4 0;4 0;-2.5 0;2.5 0;0 0"
              calcMode="spline"
              keySplines=".42 0 .58 1;.42 0 .58 1;.42 0 .58 1;.42 0 .58 1;.42 0 .58 1"
              keyTimes="0;.15;.35;.55;.75;1"
              dur=".7s"
              fill="freeze"
              additive="sum"
            />
          </template>

          <!-- rastro de luz (so voando e pousando) -->
          <g v-if="anim && (is('fly') || is('land'))">
            <circle
              v-for="(t, i) in [
                { r: 3.4, begin: '0s' },
                { r: 2.7, begin: '.2s' },
                { r: 2, begin: '.4s' },
              ]"
              :key="i"
              cx="52"
              cy="86"
              :r="t.r"
              fill="#9CF3FF"
              opacity="0"
            >
              <animateTransform
                attributeName="transform"
                type="translate"
                values="0 0;-28 7"
                dur=".6s"
                :begin="t.begin"
                :repeatCount="is('fly') ? 'indefinite' : '1'"
              />
              <animate
                attributeName="opacity"
                values=".7;0"
                dur=".6s"
                :begin="t.begin"
                :repeatCount="is('fly') ? 'indefinite' : '1'"
              />
            </circle>
          </g>

          <!-- facho: so em point -->
          <path
            d="M56 86 L34 128 Q56 138 78 128 Z"
            fill="#9CF3FF"
            :opacity="beamOpacity"
            :filter="`url(#${soft2Id})`"
            :transform="beamRotate"
          >
            <animate
              v-if="anim && is('point')"
              attributeName="opacity"
              values="0;.26"
              keyTimes="0;1"
              calcMode="spline"
              keySplines="0 0 .58 1"
              dur="1.1s"
              fill="freeze"
            />
          </path>

          <!-- asa esquerda: forma ciano da seta, amolecida em folha -->
          <g :transform="wingLTransform">
            <animateTransform
              v-if="wingsSlow"
              attributeName="transform"
              type="rotate"
              values="0 60 56;7 60 56;0 60 56"
              keyTimes="0;.5;1"
              calcMode="spline"
              keySplines=".42 0 .58 1;.42 0 .58 1"
              :dur="wingSlowDur"
              repeatCount="indefinite"
              additive="sum"
            />
            <animateTransform
              v-else-if="wingsFast"
              attributeName="transform"
              type="rotate"
              values="-14 60 56;22 60 56;-14 60 56"
              keyTimes="0;.5;1"
              calcMode="spline"
              keySplines=".42 0 .58 1;.42 0 .58 1"
              dur=".3s"
              repeatCount="indefinite"
              additive="sum"
            />
            <template v-else-if="anim && (is('land') || is('celebrate'))">
              <animateTransform
                attributeName="transform"
                type="rotate"
                values="-14 60 56;22 60 56;-14 60 56"
                keyTimes="0;.5;1"
                calcMode="spline"
                keySplines=".42 0 .58 1;.42 0 .58 1"
                dur=".3s"
                :repeatCount="is('land') ? 2 : 5"
                additive="sum"
              />
              <animateTransform
                attributeName="transform"
                type="rotate"
                values="0 60 56;7 60 56;0 60 56"
                keyTimes="0;.5;1"
                calcMode="spline"
                keySplines=".42 0 .58 1;.42 0 .58 1"
                dur="3.2s"
                :begin="is('land') ? '.6s' : '1.5s'"
                repeatCount="indefinite"
                additive="sum"
              />
            </template>
            <path
              :d="BRASA_SHAPE.wingL.d"
              :fill="BRASA_SHAPE.wingL.fill"
              :opacity="BRASA_SHAPE.wingL.opacity"
            />
          </g>

          <!-- asa direita: forma violeta da seta -->
          <g :transform="wingRTransform">
            <animateTransform
              v-if="anim && is('wave')"
              attributeName="transform"
              type="rotate"
              values="-38 63 55;-10 63 55;-38 63 55;-10 63 55;-38 63 55;-10 63 55;0 63 55"
              calcMode="spline"
              keySplines=".42 0 .58 1;.42 0 .58 1;.42 0 .58 1;.42 0 .58 1;.42 0 .58 1;.42 0 .58 1"
              keyTimes="0;.16;.32;.48;.64;.8;1"
              dur="2.2s"
              fill="freeze"
              additive="sum"
            />
            <animateTransform
              v-else-if="wingsSlow"
              attributeName="transform"
              type="rotate"
              values="0 63 55;-7 63 55;0 63 55"
              keyTimes="0;.5;1"
              calcMode="spline"
              keySplines=".42 0 .58 1;.42 0 .58 1"
              :dur="wingSlowDur"
              repeatCount="indefinite"
              additive="sum"
            />
            <animateTransform
              v-else-if="wingsFast"
              attributeName="transform"
              type="rotate"
              values="14 63 55;-22 63 55;14 63 55"
              keyTimes="0;.5;1"
              calcMode="spline"
              keySplines=".42 0 .58 1;.42 0 .58 1"
              dur=".3s"
              repeatCount="indefinite"
              additive="sum"
            />
            <template v-else-if="anim && (is('land') || is('celebrate'))">
              <animateTransform
                attributeName="transform"
                type="rotate"
                values="14 63 55;-22 63 55;14 63 55"
                keyTimes="0;.5;1"
                calcMode="spline"
                keySplines=".42 0 .58 1;.42 0 .58 1"
                dur=".3s"
                :repeatCount="is('land') ? 2 : 5"
                additive="sum"
              />
              <animateTransform
                attributeName="transform"
                type="rotate"
                values="0 63 55;-7 63 55;0 63 55"
                keyTimes="0;.5;1"
                calcMode="spline"
                keySplines=".42 0 .58 1;.42 0 .58 1"
                dur="3.2s"
                :begin="is('land') ? '.6s' : '1.5s'"
                repeatCount="indefinite"
                additive="sum"
              />
            </template>
            <path
              :d="BRASA_SHAPE.wingR.d"
              :fill="BRASA_SHAPE.wingR.fill"
              :opacity="BRASA_SHAPE.wingR.opacity"
            />
          </g>

          <!-- corpo: gota inclinada, azul da marca -->
          <path :d="BRASA_SHAPE.body.d" :fill="BRASA_SHAPE.body.fill" />
          <path
            :d="BRASA_SHAPE.shine.d"
            :fill="BRASA_SHAPE.shine.fill"
            :opacity="BRASA_SHAPE.shine.opacity"
          />

          <!-- luz do abdomen: halo difuso + nucleo -->
          <circle
            cx="56"
            cy="84"
            :r="haloR"
            fill="#9CF3FF"
            :opacity="haloOpacity"
            :filter="`url(#${softId})`"
          >
            <template v-if="anim">
              <animate
                v-if="is('idle') || is('wave')"
                attributeName="opacity"
                values=".45;.78;.45"
                keyTimes="0;.5;1"
                calcMode="spline"
                keySplines=".42 0 .58 1;.42 0 .58 1"
                dur="3.2s"
                repeatCount="indefinite"
              />
              <animate
                v-else-if="is('land')"
                attributeName="opacity"
                values=".45;.78;.45"
                keyTimes="0;.5;1"
                calcMode="spline"
                keySplines=".42 0 .58 1;.42 0 .58 1"
                dur="3.2s"
                begin=".9s"
                repeatCount="indefinite"
              />
              <template v-else-if="is('point')">
                <animate
                  attributeName="r"
                  values="13;26;22.75"
                  calcMode="spline"
                  keySplines="0 0 .58 1;0 0 .58 1"
                  keyTimes="0;.6;1"
                  dur="1.1s"
                  fill="freeze"
                />
                <animate
                  attributeName="opacity"
                  values=".5;1;.95"
                  calcMode="spline"
                  keySplines="0 0 .58 1;0 0 .58 1"
                  keyTimes="0;.6;1"
                  dur="1.1s"
                  fill="freeze"
                />
              </template>
              <template v-else-if="is('inspect')">
                <animate
                  attributeName="r"
                  values="10.4;12.4;10.4"
                  keyTimes="0;.5;1"
                  calcMode="spline"
                  keySplines=".42 0 .58 1;.42 0 .58 1"
                  dur="1.8s"
                  repeatCount="indefinite"
                />
                <animate
                  attributeName="opacity"
                  values=".85;1;.85"
                  keyTimes="0;.5;1"
                  calcMode="spline"
                  keySplines=".42 0 .58 1;.42 0 .58 1"
                  dur="1.8s"
                  repeatCount="indefinite"
                />
              </template>
              <template v-else-if="is('celebrate')">
                <animate
                  attributeName="r"
                  values="13;24.7;15.6;22.1;13"
                  calcMode="spline"
                  keySplines=".42 0 .58 1;.42 0 .58 1;.42 0 .58 1;.42 0 .58 1"
                  keyTimes="0;.22;.42;.62;1"
                  dur="1.4s"
                  fill="freeze"
                />
                <animate
                  attributeName="opacity"
                  values=".5;1;.6;1;.55"
                  calcMode="spline"
                  keySplines=".42 0 .58 1;.42 0 .58 1;.42 0 .58 1;.42 0 .58 1"
                  keyTimes="0;.22;.42;.62;1"
                  dur="1.4s"
                  fill="freeze"
                />
              </template>
              <template v-else-if="is('nudge')">
                <animate
                  attributeName="r"
                  values="13;23.4;13"
                  calcMode="spline"
                  keySplines="0 0 .58 1;0 0 .58 1"
                  keyTimes="0;.35;1"
                  dur=".7s"
                  fill="freeze"
                />
                <animate
                  attributeName="opacity"
                  values=".5;1;.55"
                  calcMode="spline"
                  keySplines="0 0 .58 1;0 0 .58 1"
                  keyTimes="0;.35;1"
                  dur=".7s"
                  fill="freeze"
                />
              </template>
              <animate
                v-else-if="is('droop')"
                attributeName="opacity"
                values=".42;.1;.4;.12;.36;.15;.42"
                keyTimes="0;.08;.16;.3;.46;.7;1"
                dur="2.4s"
                repeatCount="indefinite"
              />
            </template>
          </circle>
          <circle
            cx="56"
            cy="84"
            :r="coreR"
            fill="#9CF3FF"
            :opacity="coreOpacity"
          >
            <template v-if="anim">
              <animate
                v-if="is('point')"
                attributeName="r"
                values="6.5;8.1"
                keyTimes="0;1"
                calcMode="spline"
                keySplines="0 0 .58 1"
                dur="1.1s"
                fill="freeze"
              />
              <animate
                v-else-if="is('sleep')"
                attributeName="opacity"
                values=".22;.38;.22"
                keyTimes="0;.5;1"
                calcMode="spline"
                keySplines=".42 0 .58 1;.42 0 .58 1"
                dur="4.6s"
                repeatCount="indefinite"
              />
            </template>
          </circle>

          <!-- olhos: minimos, a expressao vem da postura e da luz -->
          <g :transform="eyesTransform">
            <animateTransform
              v-if="anim && is('wait')"
              attributeName="transform"
              type="translate"
              values="-2.4 -1.2;-2.4 -1.2;2.4 -1.2;2.4 -1.2;0 .8;0 .8;-2.4 -1.2"
              calcMode="spline"
              keySplines=".42 0 .58 1;.42 0 .58 1;.42 0 .58 1;.42 0 .58 1;.42 0 .58 1;.42 0 .58 1"
              keyTimes="0;.18;.36;.54;.72;.9;1"
              dur="3.6s"
              repeatCount="indefinite"
              additive="sum"
            />
            <circle
              cx="53"
              cy="59"
              r="2.7"
              fill="#08111F"
              :transform="eyeTransform(59)"
            />
            <circle
              cx="63.5"
              cy="62"
              r="2.7"
              fill="#08111F"
              :transform="eyeTransform(62)"
            />
          </g>

          <!-- faiscas: so na comemoracao, tres pontos de luz, sem confete -->
          <g v-if="anim && is('celebrate')">
            <circle
              v-for="(s, i) in [
                { r: 2, to: '-16 -20', begin: '0s' },
                { r: 1.8, to: '14 -24', begin: '.14s' },
                { r: 1.6, to: '3 -30', begin: '.28s' },
              ]"
              :key="i"
              cx="56"
              cy="84"
              :r="s.r"
              fill="#9CF3FF"
              opacity="0"
            >
              <animateTransform
                attributeName="transform"
                type="translate"
                :values="`0 0;${s.to}`"
                keyTimes="0;1"
                calcMode="spline"
                keySplines="0 0 .58 1"
                dur="1s"
                :begin="s.begin"
                fill="freeze"
              />
              <animate
                attributeName="opacity"
                values="0;.9;0"
                calcMode="spline"
                keySplines="0 0 .58 1;0 0 .58 1"
                keyTimes="0;.2;1"
                dur="1s"
                :begin="s.begin"
                fill="freeze"
              />
            </circle>
          </g>
        </g>
      </g>
    </g>
  </svg>
</template>
